import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { apiPost } from '../../api/apiClient';
import apiClient from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import tenantStorage from '../../utils/tenantStorage';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import '../common/rtCommon.css';
import './AddStaff.css';
import RTPageLayout from '../common/RTPageLayout';

const initialState = {
  name: '',
  dob: null,
  mobile: '',
  email: '',
  address: '',
  aadhar: '',
  residential_address: '',
  job_type: '',
  monthly_salary: '',
  copy_address: false,
};

const AddStaff = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get mode and data from location state or props
  const mode = location.state?.mode || 'add';
  const staffId = location.state?.staffId || null;
  const initialData = location.state?.initialData || null;

  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [staffTypes, setStaffTypes] = useState([]);
  const [loadingStaffTypes, setLoadingStaffTypes] = useState(true);

  const isDigits = (val) => /^\d*$/.test(val);
  const isMobile = (val) => /^\d{10}$/.test(val);
  const isAadhar = (val) => val === '' || /^\d{12}$/.test(val);
  const isEmail = (val) => val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.dob) newErrors.dob = 'Date of Birth is required';
    if (!form.mobile) newErrors.mobile = 'Mobile number is required';
    if (!form.address.trim()) newErrors.address = 'Address is required';
    if (!form.residential_address.trim())
      newErrors.residential_address = 'Residential address is required';
    if (!form.job_type) newErrors.job_type = 'Job type is required';
    if (form.mobile && !/^\d{10}$/.test(form.mobile))
      newErrors.mobile = 'Enter a valid 10-digit mobile number';
    if (form.aadhar && !/^\d{12}$/.test(form.aadhar))
      newErrors.aadhar = 'Aadhar must be 12 digits';
    if (form.aadhar && /\D/.test(form.aadhar))
      newErrors.aadhar = 'Aadhar must contain only digits';
    if (form.mobile && /\D/.test(form.mobile))
      newErrors.mobile = 'Mobile must contain only digits';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = 'Enter a valid email address';
    if (form.monthly_salary && !/^\d+(\.\d{1,2})?$/.test(form.monthly_salary))
      newErrors.monthly_salary = 'Enter a valid salary amount';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;

    if (name === 'copy_address' && checked) {
      setForm((prev) => ({
        ...prev,
        [name]: checked,
        residential_address: prev.address,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : newValue,
      }));
    }
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, dob: date }));
    setErrors((prev) => ({ ...prev, dob: undefined }));
  };

  const handleDigitsInput = (e) => {
    const { name, value } = e.target;
    let maxLength = 10;
    if (name === 'aadhar') maxLength = 12;

    const newValue = value.replace(/\D/g, '').slice(0, maxLength);
    setForm((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleReset = () => {
    setForm(initialState);
    setErrors({});
  };

  // Fetch staff types from API
  const fetchStaffTypes = async () => {
    try {
      setLoadingStaffTypes(true);
      const response = await apiClient.get('/staff/types');
      setStaffTypes(response.data);
    } catch (error) {
      console.error('Error fetching staff types:', error);
      toastError('Failed to load staff types');
      // Fallback to hardcoded types if API fails
      setStaffTypes([
        { type_id: 1, type_name: 'Manager' },
        { type_id: 2, type_name: 'Security Guard' },
        { type_id: 3, type_name: 'Cleaning Staff' },
        { type_id: 4, type_name: 'Electrician' },
        { type_id: 5, type_name: 'Plumber' },
        { type_id: 6, type_name: 'Cook' },
        { type_id: 7, type_name: 'Cook Helper' },
        { type_id: 8, type_name: 'Receptionist' },
      ]);
    } finally {
      setLoadingStaffTypes(false);
    }
  };

  // Load staff types on component mount
  useEffect(() => {
    fetchStaffTypes();
  }, []);

  // Load initial data when in edit mode
  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setForm({
        ...initialData,
        dob: initialData.dob ? new Date(initialData.dob) : null,
        copy_address: false,
      });
    }
  }, [mode, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSaving(true);
      try {
        const token = tenantStorage.getItem('token');
        const payload = {
          ...form,
          dob: form.dob ? form.dob.toISOString().split('T')[0] : null,
          aadhar: form.aadhar && form.aadhar !== '' ? form.aadhar : null,
          email: form.email && form.email !== '' ? form.email : null,
          monthly_salary:
            form.monthly_salary && form.monthly_salary !== ''
              ? parseFloat(form.monthly_salary)
              : null,
        };
        delete payload.copy_address; // Remove checkbox field from payload

        // Choose API endpoint based on mode
        const endpoint =
          mode === 'edit' ? `/staff/update/${staffId}` : '/staff/create';
        if (mode === 'edit') {
          payload.staff_id = staffId;
        }

        const response = await apiPost(endpoint, payload, {
          Authorization: `Bearer ${token}`,
        });
        if (response.status === 200 && response.data.status === 'Success') {
          toastSuccess(
            mode === 'edit'
              ? 'Staff updated successfully'
              : 'Staff created successfully',
          );
          setForm(initialState);

          if (mode === 'edit') {
            // For edit mode, go back to the staff details page
            navigate(`/staffs/view/${staffId}`);
          } else {
            // For create mode, go to the newly created staff details page
            const createdStaffId = response.data.staff_id;
            navigate(`/staffs/view/${createdStaffId}`);
          }
        } else {
          toastError(
            mode === 'edit' ? 'Error updating staff' : 'Error creating staff',
          );
        }
      } catch (err) {
        toastError(
          (mode === 'edit'
            ? 'Error updating staff: '
            : 'Error creating staff: ') +
            (err.response?.data?.message || err.message),
        );
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <RTPageLayout
      activePath={location.pathname}
      title={<>{mode === 'edit' ? 'Edit Staff' : 'Add Staff'}</>}
      actions={
        <>
          <RTButton variant="secondary" onClick={() => navigate(-1)}>
            &#8592; Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      {/* Loading Overlay for Save Operation */}
      {isSaving && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <RTSpinner
            size="medium"
            showMessage={true}
            message={
              mode === 'edit' ? 'Updating staff...' : 'Creating staff...'
            }
          />
        </div>
      )}

      <form className="add-pg-form" onSubmit={handleSubmit} noValidate>
        <div className="add-pg-sections">
          <div className="rt-section">
            <h3 className="rt-section-title">Personal Details</h3>
            <div className="form-group">
              <label>
                Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
              />
              {errors.name && <span className="form-error">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label>
                Date of Birth <span style={{ color: 'red' }}>*</span>
              </label>
              <DatePicker
                selected={form.dob}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                className="custom-datepicker"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                isClearable
                autoComplete="off"
              />
              {errors.dob && <span className="form-error">{errors.dob}</span>}
            </div>
            <div className="form-group">
              <label>
                Address <span style={{ color: 'red' }}>*</span>
              </label>
              <textarea
                name="address"
                rows={3}
                value={form.address}
                onChange={handleChange}
              />
              {errors.address && (
                <span className="form-error">{errors.address}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                Mobile Number <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                name="mobile"
                maxLength={10}
                value={form.mobile}
                onChange={handleChange}
                onInput={handleDigitsInput}
                inputMode="numeric"
              />
              {errors.mobile && (
                <span className="form-error">{errors.mobile}</span>
              )}
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
              />
              {errors.email && (
                <span className="form-error">{errors.email}</span>
              )}
            </div>
          </div>
          <div className="rt-section">
            <h3 className="rt-section-title">Work Details</h3>
            <div className="form-group">
              <label>
                Job Type <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                name="job_type"
                value={form.job_type}
                onChange={handleChange}
                disabled={loadingStaffTypes}
              >
                <option value="">
                  {loadingStaffTypes
                    ? 'Loading staff types...'
                    : 'Select Job Type'}
                </option>
                {staffTypes.map((staffType) => (
                  <option key={staffType.type_id} value={staffType.type_name}>
                    {staffType.type_name}
                  </option>
                ))}
              </select>
              {errors.job_type && (
                <span className="form-error">{errors.job_type}</span>
              )}
            </div>
            <div className="form-group">
              <label>Monthly Salary</label>
              <input
                type="text"
                name="monthly_salary"
                value={form.monthly_salary}
                onChange={handleChange}
                placeholder="Enter amount"
              />
              {errors.monthly_salary && (
                <span className="form-error">{errors.monthly_salary}</span>
              )}
            </div>
            <div className="form-group">
              <label>Aadhar Number</label>
              <input
                type="text"
                name="aadhar"
                maxLength={12}
                value={form.aadhar}
                onChange={handleChange}
                onInput={handleDigitsInput}
                inputMode="numeric"
              />
              {errors.aadhar && (
                <span className="form-error">{errors.aadhar}</span>
              )}
            </div>
            <div className="form-group">
              <label>
                Residential Address <span style={{ color: 'red' }}>*</span>
              </label>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="copy_address"
                    checked={form.copy_address}
                    onChange={handleChange}
                  />
                  Same as address
                </label>
              </div>
              <textarea
                name="residential_address"
                rows={3}
                value={form.residential_address}
                onChange={handleChange}
              />
              {errors.residential_address && (
                <span className="form-error">{errors.residential_address}</span>
              )}
            </div>
          </div>
        </div>
        <div className="add-staff-actions">
          <RTButton
            type="button"
            className="reset-btn"
            variant="secondary"
            onClick={handleReset}
            disabled={isSaving}
          >
            Reset
          </RTButton>
          <RTButton
            type="submit"
            className="save-btn"
            variant="primary"
            disabled={isSaving}
          >
            {isSaving
              ? mode === 'edit'
                ? 'Updating...'
                : 'Creating...'
              : mode === 'edit'
                ? 'Update Staff'
                : 'Save Staff'}
          </RTButton>
        </div>
      </form>
    </RTPageLayout>
  );
};

export default AddStaff;
